import { useState, useRef, useEffect } from 'react';
import { HelpCircle, ChevronDown, X, Info } from 'lucide-react';
import { DynamicIcon } from '../../lib/icons';
import { RichTextEditor } from '../ui/RichTextEditor';
import { useTranslations } from '../../i18n';
import { getExpenseTypeNameFromData, isServiceType, SERVICE_GROUP_KEY, SERVICE_SUBTYPES, SupportedLanguage, normalizeExpenseTypeKey } from '../../lib/expenseTypeHelpers';
import { FormLabel } from '../ui/FormLabel';
import defaultData from '../../data/defaultExpenseTypes.json';

export type ExpenseLineItemData = {
    id: string;
    type: string;
    title: string;
    amount: string;
    isSplitServiceCosts: boolean;
    amountParts: string;
    amountWork: string;
    fuelLiters: string;
    electricityKwh: string;
    fuelType: string;
    description: string;
    is_initial_purchase?: boolean;
};

type ExpenseLineItemProps = {
    item: ExpenseLineItemData;
    onChange: (id: string, field: keyof ExpenseLineItemData, value: any) => void;
    onRemove?: (id: string) => void;
    isReadOnly: boolean;
    availableExpenseTypes: any[];
    selectedCar: any;
    effectivePreferences: any;
    currentLanguage: SupportedLanguage;
    variant?: 'card' | 'flat';
    children?: React.ReactNode;
};

export function ExpenseLineItem({
    item,
    onChange,
    onRemove,
    isReadOnly,
    availableExpenseTypes,
    selectedCar,
    effectivePreferences,
    currentLanguage,
    variant = 'card',
    children
}: ExpenseLineItemProps) {
    const { t } = useTranslations();
    const [isTypePickerOpen, setIsTypePickerOpen] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const normalizedType = normalizeExpenseTypeKey(item.type);

    // Close tooltip on click outside or scroll
    useEffect(() => {
        function handleDismiss() {
            setTooltipOpen(false);
        }

        function handleClickOutside(event: MouseEvent) {
            const target = event.target as Node;
            const isTrigger = target instanceof Element && (target.closest('[data-tooltip-trigger]') !== null);
            
            if (tooltipRef.current && !tooltipRef.current.contains(target) && !isTrigger) {
                handleDismiss();
            }
        }

        if (tooltipOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('scroll', handleDismiss, true);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleDismiss, true);
        };
    }, [tooltipOpen]);

    const getTypeDescription = (key: string) => {
        if (!key) return null;
        if (key === SERVICE_GROUP_KEY || isServiceType(key)) {
            return (
                <div className="space-y-3">
                    {SERVICE_SUBTYPES.map(subtype => {
                        const item = (defaultData as any)[subtype];
                        const name = item?.translations[currentLanguage] || item?.translations['en'] || subtype;
                        const desc = item?.descriptions?.[currentLanguage] || item?.descriptions?.['en'];
                        if (!desc) return null;
                        return (
                            <div key={subtype} className="space-y-0.5">
                                <div className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: item?.color }} />
                                    {name}
                                </div>
                                <div className="text-[10px] leading-tight opacity-80 pl-3.5 border-l border-gray-100 dark:border-white/5">
                                    {desc}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }
        const normalizedKey = normalizeExpenseTypeKey(key);
        const item = (defaultData as any)[normalizedKey];
        return item?.descriptions?.[currentLanguage] || item?.descriptions?.['en'] || null;
    };

    const handleAmountChange = (val: string) => {
        onChange(item.id, 'amount', val);
    };

    return (
        <div className={variant === 'card' ? "bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-2xl px-4 pb-4 pt-0 space-y-4 relative" : "space-y-4 relative mt-2"}>
            {onRemove && !isReadOnly && (
                <button
                    type="button"
                    onClick={() => onRemove(item.id)}
                    className="absolute -top-3 -right-3 w-8 h-8 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors z-10"
                >
                    <X size={16} strokeWidth={2.5} />
                </button>
            )}

            <div className={item.type === 'fuel' && selectedCar?.fuel_type !== 'electric' ? "grid grid-cols-2 gap-3 items-start mt-2" : "mt-3"}>
                <div>
                    <div 
                        className={`flex items-center gap-1.5 mb-1 ${item.type && getTypeDescription(item.type) ? 'cursor-pointer group w-fit' : ''}`}
                        onClick={(e) => {
                            if (item.type && getTypeDescription(item.type)) {
                                e.stopPropagation();
                                setTooltipOpen(!tooltipOpen);
                            }
                        }}
                    >
                        <FormLabel className={`block text-sm font-semibold text-gray-700 dark:text-gray-300 font-primary ${item.type && getTypeDescription(item.type) ? 'group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors' : ''}`}>
                            {t.expenses.type} {!item.type && <span className="text-red-500 ml-1">*</span>}
                        </FormLabel>
                        {item.type && getTypeDescription(item.type) && (
                            <div className="relative">
                                <div
                                    className="p-1 text-blue-500 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 rounded-lg transition-colors flex items-center justify-center translate-y-[-1px]"
                                >
                                    <Info size={14} />
                                </div>
                                {tooltipOpen && (
                                    <div 
                                        ref={tooltipRef}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setTooltipOpen(false);
                                        }}
                                        className="absolute top-full left-0 mt-2 w-64 p-3 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-white/10 z-[100] text-xs leading-relaxed text-gray-600 dark:text-gray-300 pointer-events-auto cursor-pointer animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-200"
                                    >
                                        <div className="font-bold text-gray-900 dark:text-white mb-1">
                                            {item.type === 'unassigned' ? 'Unassigned' : isServiceType(item.type) ? ((t.expenses as any).serviceGroup?.name || 'Service') : (getExpenseTypeNameFromData(availableExpenseTypes.find((t: any) => t.type_key === item.type), currentLanguage) || (t.expenses.typeLabels as any)[item.type] || item.type)}
                                        </div>
                                        {getTypeDescription(item.type)}
                                        <div className="absolute bottom-full left-3 w-3 h-3 bg-white dark:bg-slate-800 border-l border-t border-gray-200 dark:border-white/10 rotate-45 -mb-1.5" />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button type="button" disabled={isReadOnly} onClick={() => setIsTypePickerOpen(true)} className="flex-1 px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl flex items-center justify-between hover:border-gray-300 dark:hover:border-white/20 transition-all active:scale-[0.99] disabled:bg-gray-50 dark:disabled:bg-slate-900/20">
                            <div className="flex items-center gap-3">
                                {item.type ? (
                                    <>
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${isServiceType(normalizedType) ? '#34D399' : (normalizedType === 'unassigned' ? '#EF4444' : availableExpenseTypes.find((t: any) => t.type_key === normalizedType)?.color)}15` }}>
                                            {normalizedType === 'unassigned' ? (
                                                <HelpCircle size={20} className="text-red-500" />
                                            ) : isServiceType(normalizedType) ? (
                                                <DynamicIcon name="wrench" size={20} color="#34D399" />
                                            ) : (
                                                <DynamicIcon name={availableExpenseTypes.find((t: any) => t.type_key === normalizedType)?.icon || 'Circle'} size={20} color={availableExpenseTypes.find((t: any) => t.type_key === normalizedType)?.color || '#6B7280'} />
                                            )}
                                        </div>
                                        <span className="text-gray-900 dark:text-gray-100 font-semibold">{normalizedType === 'unassigned' ? 'Unassigned' : isServiceType(normalizedType) ? ((t.expenses as any).serviceGroup?.name || 'Service') : (getExpenseTypeNameFromData(availableExpenseTypes.find((t: any) => t.type_key === normalizedType), currentLanguage) || (t.expenses.typeLabels as any)[normalizedType] || normalizedType)}</span>
                                    </>
                                ) : <span className="text-gray-400">{t.expenses.selectTypePlaceholder}</span>}
                            </div>
                            {!isReadOnly && <ChevronDown size={20} className="text-gray-400 shrink-0" />}
                        </button>
                    </div>

                    {/* Service Subtype Segmented Control */}
                    {normalizedType && isServiceType(normalizedType) && (
                        <div className="mt-3 flex gap-2 p-1.5 bg-gray-100 dark:bg-slate-900/60 rounded-xl overflow-x-auto">
                            {SERVICE_SUBTYPES.map(subtypeKey => {
                                const subtypeDef = availableExpenseTypes.find((t: any) => t.type_key === subtypeKey);
                                const isActive = item.type === subtypeKey;
                                const label = subtypeDef ? (getExpenseTypeNameFromData(subtypeDef, currentLanguage) || (t.expenses.typeLabels as any)[subtypeKey]) : subtypeKey;
                                const icon = subtypeDef?.icon || 'Wrench';
                                const color = subtypeDef?.color || '#3b82f6';
                                return (
                                    <button
                                        key={subtypeKey}
                                        type="button"
                                        disabled={isReadOnly}
                                        onClick={() => onChange(item.id, 'type', subtypeKey)}
                                        className={`flex-1 min-w-0 py-3 px-2 text-xs sm:text-sm font-semibold rounded-xl transition-all flex flex-col items-center justify-center gap-2 ${isActive ? 'bg-white dark:bg-[#000D19] text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-white/5'}`}
                                    >
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-transform ${isActive ? 'scale-110 shadow-sm' : 'scale-100 opacity-70 grayscale-[20%]'}`} style={{ backgroundColor: `${color}15` }}>
                                            <DynamicIcon name={icon} size={20} color={color} />
                                        </div>
                                        <span className="block truncate w-full text-center px-1">{label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {item.type === 'fuel' && selectedCar?.fuel_type !== 'electric' && (
                    <div className="flex-1">
                        <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary">
                            {t.expenses.fuelType}
                        </FormLabel>
                        <div className="relative">
                            <select value={item.fuelType} onChange={(e) => onChange(item.id, 'fuelType', e.target.value)} disabled={isReadOnly} className="w-full h-[66px] px-4 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white appearance-none">
                                <option value="petrol">{t.expenses.fuelTypeOptions.petrol}</option>
                                <option value="diesel">{t.expenses.fuelTypeOptions.diesel}</option>
                                <option value="lpg">{t.expenses.fuelTypeOptions.lpg}</option>
                                {selectedCar?.fuel_type === 'hybrid' && <option value="electricity">{t.expenses.fuelTypeOptions.electricity}</option>}
                            </select>
                            {!isReadOnly && <ChevronDown size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />}
                        </div>
                    </div>
                )}
            </div>

            {children}

            {item.type && item.type !== 'info' && (
                <>
                    {isServiceType(item.type) && item.isSplitServiceCosts ? (
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 font-primary">
                                    {t.expenses.amount} ({effectivePreferences?.currency_symbol || '€'})
                                </FormLabel>
                                {!isReadOnly && (
                                    <button
                                        type="button"
                                        onClick={() => onChange(item.id, 'isSplitServiceCosts', false)}
                                        className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 font-semibold"
                                    >
                                        {t.expenses.combineCosts}
                                    </button>
                                )}
                            </div>
                            <div className="flex gap-2 sm:gap-3">
                                <div className="flex-1">
                                    <FormLabel className="block text-[11px] sm:text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                        {t.expenses.amountParts}
                                    </FormLabel>
                                    <input
                                        type="number" step="0.01" min="0" disabled={isReadOnly}
                                        value={item.amountParts}
                                        onChange={(e) => {
                                            onChange(item.id, 'amountParts', e.target.value);
                                            const parts = parseFloat(e.target.value) || 0;
                                            const work = parseFloat(item.amountWork) || 0;
                                            if (e.target.value || item.amountWork) {
                                                handleAmountChange((parts + work).toFixed(2).replace(/\.00$/, ''));
                                            } else {
                                                handleAmountChange('');
                                            }
                                        }}
                                        className="w-full px-3 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary text-sm"
                                        placeholder="0.00"
                                    />
                                </div>
                                <div className="flex-1">
                                    <FormLabel className="block text-[11px] sm:text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                        {t.expenses.amountWork}
                                    </FormLabel>
                                    <input
                                        type="number" step="0.01" min="0" disabled={isReadOnly}
                                        value={item.amountWork}
                                        onChange={(e) => {
                                            onChange(item.id, 'amountWork', e.target.value);
                                            const parts = parseFloat(item.amountParts) || 0;
                                            const work = parseFloat(e.target.value) || 0;
                                            if (e.target.value || item.amountParts) {
                                                handleAmountChange((parts + work).toFixed(2).replace(/\.00$/, ''));
                                            } else {
                                                handleAmountChange('');
                                            }
                                        }}
                                        className="w-full px-3 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary text-sm"
                                        placeholder="0.00"
                                    />
                                </div>
                                <div className="flex-1">
                                    <FormLabel className="block text-[11px] sm:text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                        {t.expenses.totalAmount}
                                    </FormLabel>
                                    <input
                                        type="number" disabled
                                        value={item.amount}
                                        className="w-full px-3 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-white/5 rounded-xl transition-all font-primary font-bold text-gray-800 dark:text-gray-200 text-sm opacity-80"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            {item.type === 'fuel' ? (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary">
                                            {t.expenses.amount} ({effectivePreferences?.currency_symbol || '€'})
                                        </FormLabel>
                                        <input type="number" step="0.01" disabled={isReadOnly} value={item.amount} onChange={(e) => handleAmountChange(e.target.value)} min="0" className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500" placeholder="0.00" />
                                    </div>
                                    <div>
                                        <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary">
                                            {effectivePreferences?.volume_unit === 'gallons' ? t.settings.gallons : t.settings.liters}
                                        </FormLabel>
                                        <input type="number" step="0.01" disabled={isReadOnly} value={item.fuelLiters} onChange={(e) => onChange(item.id, 'fuelLiters', e.target.value)} required min="0" className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500" placeholder="0.00" />
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 font-primary">
                                            {t.expenses.amount} ({effectivePreferences?.currency_symbol || '€'})
                                        </FormLabel>
                                        {isServiceType(item.type) && !isReadOnly && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (!item.amountParts && !item.amountWork && item.amount) {
                                                        onChange(item.id, 'amountParts', item.amount);
                                                    }
                                                    onChange(item.id, 'isSplitServiceCosts', true);
                                                }}
                                                className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 font-semibold"
                                            >
                                                {t.expenses.splitCosts}
                                            </button>
                                        )}
                                    </div>
                                    <input type="number" step="0.01" disabled={isReadOnly} value={item.amount} onChange={(e) => handleAmountChange(e.target.value)} min="0" className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500" placeholder="0.00" />
                                </div>
                            )}
                        </div>
                    )}
                    {item.type === 'electricity' && (
                        <div>
                            <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary">
                                kWh
                            </FormLabel>
                            <input type="number" step="0.01" disabled={isReadOnly} value={item.electricityKwh} onChange={(e) => onChange(item.id, 'electricityKwh', e.target.value)} min="0" className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-primary disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500" placeholder="0.00" />
                        </div>
                    )}
                </>
            )}

            <div>
                <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 font-primary">
                    {t.expenses.detailedDescription}
                </FormLabel>
                <RichTextEditor
                    content={item.description}
                    onChange={(val) => onChange(item.id, 'description', val)}
                    readOnly={isReadOnly}
                    placeholder={t.expenses.detailedDescription}
                    minHeight="80px"
                    maxHeight="250px"
                />
            </div>

            {isTypePickerOpen && (() => {
                const pickerItems: any[] = [];
                let serviceGroupInserted = false;
                for (const et of availableExpenseTypes) {
                    if (isServiceType(et.type_key)) {
                        if (!serviceGroupInserted) {
                            pickerItems.push({ type_key: SERVICE_GROUP_KEY, icon: 'wrench', color: '#34D399', _isGroup: true });
                            serviceGroupInserted = true;
                        }
                    } else {
                        pickerItems.push(et);
                    }
                }

                const handlePickerItemClick = (et: any) => {
                    if (et._isGroup) {
                        onChange(item.id, 'type', SERVICE_SUBTYPES[0]);
                        setIsTypePickerOpen(false);
                    } else {
                        onChange(item.id, 'type', et.type_key);
                        setIsTypePickerOpen(false);
                    }
                };

                const serviceGroupLabel = (t.expenses as any).serviceGroup?.name || 'Service';

                return (
                    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
                        <div className="bg-white dark:bg-[#000D19] w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl overflow-hidden flex flex-col h-[80vh] sm:h-auto max-h-[85vh] animate-in slide-in-from-bottom duration-300">
                            <div className="p-4 border-b border-gray-100 dark:border-white/5 flex items-center justify-between bg-white dark:bg-[#000D19] sticky top-0 z-10">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white flex-1">{t.expenses.selectType}</h3>
                                <button onClick={() => setIsTypePickerOpen(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors"><X size={24} className="text-gray-500" /></button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4">
                                <div className="grid grid-cols-3 gap-3">
                                    {pickerItems.map((et: any) => {
                                        if (et._isGroup) {
                                            const isActive = isServiceType(item.type);
                                            return (
                                                <button key={SERVICE_GROUP_KEY} onClick={() => handlePickerItemClick(et)} className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-all ${isActive ? 'bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500 dark:ring-blue-400' : 'hover:bg-gray-50 dark:hover:bg-white/5 border border-gray-100 dark:border-white/5'}`}>
                                                    <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 mb-1" style={{ backgroundColor: '#34D39915' }}>
                                                        <DynamicIcon name="wrench" size={20} color="#34D399" />
                                                    </div>
                                                    <span className={`text-[12px] font-bold text-center leading-tight ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>{serviceGroupLabel}</span>
                                                </button>
                                            );
                                        }
                                        return (
                                            <button key={et.type_key} onClick={() => handlePickerItemClick(et)} className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-all ${normalizedType === et.type_key ? 'bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500 dark:ring-blue-400' : 'hover:bg-gray-50 dark:hover:bg-white/5 border border-gray-100 dark:border-white/5'}`}>
                                                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 mb-1" style={{ backgroundColor: `${et.color || '#6B7280'}15` }}>
                                                    <DynamicIcon name={et.icon || 'Circle'} size={20} color={et.color || '#6B7280'} />
                                                </div>
                                                <span className={`text-[12px] font-bold text-center leading-tight ${normalizedType === et.type_key ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>{getExpenseTypeNameFromData(et, currentLanguage) || (t.expenses.typeLabels as any)[et.type_key] || et.type_key}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}
